import { useId, type ComponentPropsWithRef, type ReactNode } from "react";
import { cn } from "./cn";
import {
  controlClasses,
  describedBy,
  FieldShell,
  type ControlSize,
} from "./Field";

const SELECT_CHEVRON = {
  xs: { padding: "pr-6", offset: "right-1.5 text-[13px]" },
  sm: { padding: "pr-9", offset: "right-3" },
  md: { padding: "pr-9", offset: "right-3" },
  lg: { padding: "pr-9", offset: "right-3" },
} as const satisfies Record<ControlSize, { padding: string; offset: string }>;

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  ComponentPropsWithRef<"select">,
  "size"
> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  options?: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}

export function Select({
  label,
  hint,
  error,
  size = "md",
  options,
  placeholder,
  containerClassName,
  className,
  id,
  required,
  children,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative">
        <select
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fieldId, !!error, !!hint)}
          className={cn(
            controlClasses(!!error, size),
            "appearance-none",
            SELECT_CHEVRON[size].padding,
            className,
          )}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options?.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <i
          className={cn(
            "ri-arrow-down-s-line pointer-events-none absolute top-1/2 -translate-y-1/2 text-content-muted",
            SELECT_CHEVRON[size].offset,
          )}
        />
      </div>
    </FieldShell>
  );
}
